import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search, ExternalLink, RefreshCcw } from "lucide-react";

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/catalog").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(
    (t) => t.tool_name?.toLowerCase().includes(q.toLowerCase()) || t.category?.toLowerCase().includes(q.toLowerCase())
  );

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="eyebrow text-gold">Knowledge Base</div>
          <h1 className="font-display text-5xl text-navy mt-3">Tool Catalog</h1>
          <div className="w-12 h-[2px] bg-gold mt-4" />
          <p className="text-navy/60 mt-4 max-w-2xl">
            Every tool you've recommended, with live-refreshed monthly pricing pulled via web search.
            The catalog grows automatically each time you run an assessment.
          </p>
        </div>

        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
          <Input data-testid="catalog-search" placeholder="Search tools or categories..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-10 rounded-none bg-white border-navy/15" />
        </div>

        {loading ? (
          <div className="text-navy/50">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-navy/10 p-16 text-center">
            <RefreshCcw className="w-10 h-10 text-gold mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-navy">No tools yet</h3>
            <p className="text-navy/60 mt-2">Run an assessment to start building your catalog.</p>
          </div>
        ) : (
          <div className="bg-white border border-navy/10" data-testid="catalog-table">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-navy/10 eyebrow text-navy/60 text-[10px]">
              <div className="col-span-4">Tool</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2 text-right">Monthly Cost</div>
              <div className="col-span-2 text-right">Times Recommended</div>
              <div className="col-span-2 text-right">Last Refreshed</div>
            </div>
            {filtered.map((t) => (
              <div key={t.key} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-navy/5 items-center hover:bg-ivory/50">
                <div className="col-span-4">
                  <div className="font-display text-lg text-navy">{t.tool_name}</div>
                  {t.source_url && (
                    <a href={t.source_url} target="_blank" rel="noreferrer"
                      className="text-xs text-gold hover:underline inline-flex items-center gap-1 mt-1">
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="col-span-2">
                  {t.category ? <Badge variant="outline" className="rounded-none text-xs border-navy/20">{t.category}</Badge> : <span className="text-navy/30 text-xs">—</span>}
                </div>
                <div className="col-span-2 text-right font-display text-lg text-navy">
                  ${Number(t.monthly_cost || 0).toFixed(0)}<span className="text-xs text-navy/40">/mo</span>
                </div>
                <div className="col-span-2 text-right text-sm text-navy/70">
                  {t.seen_count || 0}
                </div>
                <div className="col-span-2 text-right text-xs text-navy/50">
                  {fmtDate(t.last_updated)}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-navy/40 mt-6">
          Prices are cached for 7 days. Older entries refresh automatically the next time the tool is recommended.
        </p>
      </div>
    </Layout>
  );
}
